<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  transactions: {
    type: Array,
    default: () => [],
  },
  pagination: {
    type: Object,
    default: () => ({
      current_page: 1,
      per_page: 25,
      total_pages: 0,
      total_count: 0,
    }),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['page-change']);
const { t } = useI18n();

const kindLabels = {
  subscription: t('BILLING_SETTINGS.HISTORY.KIND.SUBSCRIPTION'),
  topup: t('BILLING_SETTINGS.HISTORY.KIND.TOPUP'),
};
const statusLabels = {
  pending: t('BILLING_SETTINGS.HISTORY.STATUS.PENDING'),
  remote_outcome_unknown: t(
    'BILLING_SETTINGS.HISTORY.STATUS.REMOTE_OUTCOME_UNKNOWN'
  ),
  paid: t('BILLING_SETTINGS.HISTORY.STATUS.PAID'),
  refunded: t('BILLING_SETTINGS.HISTORY.STATUS.REFUNDED'),
  disputed: t('BILLING_SETTINGS.HISTORY.STATUS.DISPUTED'),
  failed: t('BILLING_SETTINGS.HISTORY.STATUS.FAILED'),
  expired: t('BILLING_SETTINGS.HISTORY.STATUS.EXPIRED'),
};

const canGoPrevious = computed(() => props.pagination.current_page > 1);
const canGoNext = computed(
  () => props.pagination.current_page < props.pagination.total_pages
);

const formatAmount = transaction =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: transaction.currency,
  }).format(transaction.amount_cents / 100);

const formatDate = value =>
  value
    ? new Intl.DateTimeFormat(undefined, { timeZone: 'UTC' }).format(
        new Date(value)
      )
    : '';

const safeFinancialUrl = value => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname && !url.username
      ? url.href
      : null;
  } catch {
    return null;
  }
};

// eslint-disable-next-line vue/custom-event-name-casing
const changePage = page => emit('page-change', page);
</script>

<template>
  <div class="grid gap-4">
    <div
      v-if="loading"
      class="grid gap-2"
      role="status"
      :aria-label="$t('BILLING_SETTINGS.HISTORY.LOADING')"
    >
      <div
        v-for="row in 3"
        :key="row"
        class="h-10 animate-pulse rounded-lg bg-n-solid-3"
      />
    </div>

    <div
      v-else-if="!transactions.length"
      class="rounded-lg bg-n-solid-2 p-5 text-sm text-n-slate-11"
    >
      {{ $t('BILLING_SETTINGS.HISTORY.EMPTY') }}
    </div>

    <div v-else class="overflow-x-auto">
      <table class="w-full min-w-[680px] text-left text-sm">
        <thead class="text-xs text-n-slate-10">
          <tr>
            <th class="pb-3 font-medium">
              {{ $t('BILLING_SETTINGS.HISTORY.DATE') }}
            </th>
            <th class="pb-3 font-medium">
              {{ $t('BILLING_SETTINGS.HISTORY.TYPE') }}
            </th>
            <th class="pb-3 font-medium">
              {{ $t('BILLING_SETTINGS.HISTORY.STATUS_LABEL') }}
            </th>
            <th class="pb-3 text-right font-medium">
              {{ $t('BILLING_SETTINGS.HISTORY.AMOUNT') }}
            </th>
            <th class="pb-3 text-right font-medium">
              {{ $t('BILLING_SETTINGS.HISTORY.DOCUMENTS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="transaction in transactions"
            :key="transaction.id"
            class="border-t border-n-weak text-n-slate-11"
          >
            <td class="py-3">{{ formatDate(transaction.created_at) }}</td>
            <td class="py-3 text-n-slate-12">
              {{ kindLabels[transaction.kind] }}
            </td>
            <td class="py-3">
              {{ statusLabels[transaction.status] }}
            </td>
            <td class="py-3 text-right tabular-nums text-n-slate-12">
              {{ formatAmount(transaction) }}
            </td>
            <td class="py-3 text-right">
              <div class="flex justify-end gap-3">
                <a
                  v-if="safeFinancialUrl(transaction.receipt_url)"
                  data-test="financial-document"
                  :href="safeFinancialUrl(transaction.receipt_url)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-medium text-n-blue-11 hover:underline"
                >
                  {{ $t('BILLING_SETTINGS.HISTORY.RECEIPT') }}
                </a>
                <a
                  v-if="safeFinancialUrl(transaction.invoice_url)"
                  data-test="financial-document"
                  :href="safeFinancialUrl(transaction.invoice_url)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-medium text-n-blue-11 hover:underline"
                >
                  {{ $t('BILLING_SETTINGS.HISTORY.INVOICE') }}
                </a>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="pagination.total_pages > 1"
      class="flex items-center justify-between gap-3"
    >
      <Button
        data-test="history-previous"
        size="sm"
        color="slate"
        variant="outline"
        :label="$t('BILLING_SETTINGS.HISTORY.PREVIOUS')"
        :disabled="loading || !canGoPrevious"
        @click="changePage(pagination.current_page - 1)"
      />
      <span class="text-xs tabular-nums text-n-slate-10">
        {{
          $t('BILLING_SETTINGS.HISTORY.PAGE', {
            current: pagination.current_page,
            total: pagination.total_pages,
          })
        }}
      </span>
      <Button
        data-test="history-next"
        size="sm"
        color="slate"
        variant="outline"
        :label="$t('BILLING_SETTINGS.HISTORY.NEXT')"
        :disabled="loading || !canGoNext"
        @click="changePage(pagination.current_page + 1)"
      />
    </div>
  </div>
</template>
